import { compile, type Scope, type Value } from './expression.ts';

/** A rectangle in the coordinates of the layout's parent. */
export interface Box {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * The constraints of one child, as expression strings: the ten attached properties of the legacy
 * `ConstraintLayout` (`Ref`, `Left`, `Right`, `Top`, `Bottom`, `W`, `H`, `Center`, `CenterX`,
 * `CenterY`). `center` must evaluate to a point (`{ x, y }`), the others to numbers.
 */
export interface Constraints {
    ref?: string;
    left?: string;
    right?: string;
    top?: string;
    bottom?: string;
    w?: string;
    h?: string;
    center?: string;
    centerX?: string;
    centerY?: string;
}

/**
 * Where a child actually draws inside its box, relative to the box (the legacy `IHasRenderBounds`,
 * for example the ink of a fitted text). Later children refer to these bounds, not to the box.
 */
export type RenderBounds = (index: number, box: Box) => Box | undefined;

function number(source: string | undefined, scope: Scope) : number | undefined {
    if (source === undefined) {
        return undefined;
    }

    const value = compile(source).evaluate(scope);

    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function point(source: string | undefined, scope: Scope) : { x: number; y: number } | undefined {
    if (source === undefined) {
        return undefined;
    }

    const value = compile(source).evaluate(scope);
    if (value === undefined || typeof value === 'number') {
        return undefined;
    }

    const x = value.x ?? value.X;
    const y = value.y ?? value.Y;

    return typeof x === 'number' && typeof y === 'number' ? { x, y } : undefined;
}

function boundsValue(left: number, top: number, width: number, height: number) : Value {
    return {
        left,
        top,
        right: left + width,
        bottom: top + height,
        width,
        height,
        center: { x: left + width / 2, y: top + height / 2 },
    };
}

/** Resolves the constraints of one child, with the precedence of the legacy `Constraint.Parse`. */
function resolve(constraints: Constraints, scope: Scope) : Box | null {
    const center = point(constraints.center, scope);
    const centerX = number(constraints.centerX, scope) ?? center?.x;
    const centerY = number(constraints.centerY, scope) ?? center?.y;
    let left = number(constraints.left, scope);
    let right = number(constraints.right, scope);
    let top = number(constraints.top, scope);
    let bottom = number(constraints.bottom, scope);
    let width = number(constraints.w, scope);
    let height = number(constraints.h, scope);

    // The order matters, and a centre wins over the opposite edge: that is how the legacy code is
    // written, and dashboards were tuned against it.
    if (top === undefined && height !== undefined) {
        if (bottom !== undefined) {
            top = bottom - height;
        }
        if (centerY !== undefined) {
            top = centerY - height / 2;
        }
    }
    if (bottom === undefined && height !== undefined) {
        if (top !== undefined) {
            bottom = top + height;
        }
        if (centerY !== undefined) {
            bottom = centerY + height / 2;
        }
    }
    if (left === undefined && width !== undefined) {
        if (right !== undefined) {
            left = right - width;
        }
        if (centerX !== undefined) {
            left = centerX - width / 2;
        }
    }
    if (right === undefined && width !== undefined) {
        if (left !== undefined) {
            right = left + width;
        }
        if (centerX !== undefined) {
            right = centerX + width / 2;
        }
    }
    if (left === undefined || top === undefined || right === undefined || bottom === undefined) {
        return null;
    }

    // The edges given or derived above decide the size where it was not given.
    return { left, top, width: width ?? right - left, height: height ?? bottom - top };
}

/**
 * Lays out the children of a constraint layout of the given size, in one ordered pass: each child
 * may refer to `parent` and to the children before it that have a `ref`. A child whose
 * constraints cannot be resolved gets `null` and is not shown, as in the legacy layout.
 */
export function solve(
    parent: { width: number; height: number },
    children: readonly Constraints[],
    renderBounds?: RenderBounds,
) : (Box | null)[] {
    const scope: Record<string, Value> = { parent: boundsValue(0, 0, parent.width, parent.height) };

    return children.map((constraints, index) => {
        const box = resolve(constraints, scope);
        if (box && constraints.ref !== undefined) {
            const inner = renderBounds?.(index, box) ?? { left: 0, top: 0, width: box.width, height: box.height };
            scope[constraints.ref] = boundsValue(box.left + inner.left, box.top + inner.top, inner.width, inner.height);
        }
        return box;
    });
}
